<template>
  <div class="w-full flex flex-col items-center justify-center min-h-[100px] py-24 mb-32">
    <div v-if="loading" class="flex items-center justify-center h-full">
      <SparkSpinner />
    </div>
    <div v-else class="w-full relative">
      <div class="h-64 pt-16 overflow-visible">
        <Bar ref="chartRef" :data="chartData" :options="chartOptions" />
        <div class="absolute top-0 right-0">
          <p class="text-primary-500 hover:cursor-pointer hover:text-primary-600" @click="downloadChart" data-pdf-hide>
            <i class="fa-solid fa-download mr-4"></i>Download Chart
          </p>
        </div>
      </div>

      <!-- Legend -->
      <div v-if="sortedSegmentsWithCustomColors && sortedSegmentsWithCustomColors.length" class="w-full mt-8 bg-white">
        <!-- Total Row -->
        <div class="flex items-center" style="height: 25px">
          <span class="font-bold text-12 text-navigation font-sans">Total</span>
          <span
            class="font-bold text-12 text-navigation font-sans ml-auto"
            v-html="`${totalValue} ${formattedTotalUnit}`"
          ></span>
        </div>

        <!-- Separator Line -->
        <div class="w-full h-px bg-gray-200 my-2"></div>

        <!-- Segment Rows -->
        <div>
          <div
            v-for="(segment, index) in sortedSegmentsWithCustomColors"
            :key="segment.label + index"
            class="flex items-center justify-end"
            style="height: 25px"
          >
            <!-- Color Box -->
            <div
              class="flex-shrink-0"
              :style="{
                width: '15px',
                height: '15px',
                backgroundColor: segment.color,
                marginRight: '10px',
              }"
            ></div>
            <!-- Label with truncation exactly like download -->
            <span
              class="text-12 text-navigation font-sans w-1/5"
              :style="{ fontWeight: 'normal' }"
              :title="segment.label"
            >
              {{ truncateLabel(segment.label) }}
            </span>
            <!-- Value -->
            <span
              class="text-12 text-gray-500 font-sans w-1/5 text-right"
              :style="{ fontWeight: 'normal' }"
              v-html="`${segment.value} ${formattedTotalUnit}`"
            ></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import SparkSpinner from '@/components/SparkComponents/SparkSpinner.vue';
import SparkButton from '@/components/SparkComponents/SparkButton.vue';
import logoUrl from '@/assets/img/Logo_Positive_Transparent.png';
import { formatCO2Unit } from '@/utils/carbonCalculation';
import {
  calculateSegmentsTotal,
  generateCarbonChartPartData,
  generateCarbonChartPartOptions,
  generateSingleChainLegend,
  truncateLabel as utilTruncateLabel,
} from '@/utils/carbonChartUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default {
  name: 'CarbonChartOneChain',
  components: { Bar, SparkSpinner, SparkButton },
  props: {
    segments: {
      type: Array,
      required: true, // [{ label, value, color, tooltip }]
    },
    loading: {
      type: Boolean,
      default: false,
    },
    totalValue: {
      type: [String, Number],
      default: '',
    },
    totalUnit: {
      type: String,
      default: '',
    },
    totalSegmentIndex: {
      type: Number,
      default: 0,
    },
    title: {
      type: String,
      default: '',
    },
  },
  computed: {
    formattedTotalUnit() {
      return formatCO2Unit(this.totalUnit);
    },
    total() {
      return calculateSegmentsTotal(this.segments);
    },
    chartData() {
      return generateCarbonChartPartData(this.segments);
    },
    chartOptions() {
      return generateCarbonChartPartOptions(this.segments, this.totalUnit);
    },
    sortedSegmentsWithCustomColors() {
      return generateSingleChainLegend(this.segments);
    },
  },
  methods: {
    truncateLabel(label) {
      const maxLabelWidth = 200 - 35; // labelColumnWidth - space for color box and spacing
      return utilTruncateLabel(label, maxLabelWidth);
    },
    async downloadChart() {
      // Create a new canvas for the high-res chart
      const exportCanvas = document.createElement('canvas');
      const exportCtx = exportCanvas.getContext('2d');
      const chartScale = 2; // Scale factor for the chart

      // Set the export canvas size
      exportCanvas.width = this.$refs.chartRef.$el.width * chartScale;
      exportCanvas.height = this.$refs.chartRef.$el.height * chartScale;
      exportCtx.scale(chartScale, chartScale);

      // Create a new chart instance for export
      const exportChart = new ChartJS(exportCtx, {
        type: 'bar',
        data: this.chartData,
        options: {
          ...this.chartOptions,
          animation: false,
          responsive: false,
          maintainAspectRatio: false,
        },
      });

      // Wait for the chart to render
      await new Promise(resolve => setTimeout(resolve, 100));

      const chartImage = new Image();
      chartImage.src = exportCanvas.toDataURL('image/png', 1.0);

      const logoImage = new Image();
      logoImage.src = logoUrl;

      await Promise.all([
        new Promise(resolve => (chartImage.onload = resolve)),
        new Promise(resolve => (logoImage.onload = resolve)),
      ]);

      // Create a new canvas for the composite image
      const compositeCanvas = document.createElement('canvas');
      const ctx = compositeCanvas.getContext('2d');

      // Set canvas size to accommodate title, chart, logo, and legend
      const padding = 20;
      const chartPadding = 40; // Additional padding for the chart
      const logoWidth = 100; // Set desired width first
      const logoHeight = (logoImage.height * logoWidth) / logoImage.width; // Calculate height based on aspect ratio
      const legendHeight = this.sortedSegmentsWithCustomColors.length * 25 + padding;
      const titleHeight = this.title ? 30 : 0; // Add space for title if it exists
      const baseWidth = this.$refs.chartRef.$el.width;
      const baseHeight = this.$refs.chartRef.$el.height + logoHeight + legendHeight + titleHeight + padding * 2;

      // Scale up the canvas for better quality
      const scale = 2; // Scale factor for higher quality
      compositeCanvas.width = baseWidth * scale;
      compositeCanvas.height = baseHeight * scale;
      ctx.scale(scale, scale);

      // Fill background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, baseWidth, baseHeight);

      // Draw title if it exists
      let currentY = padding;
      if (this.title) {
        ctx.fillStyle = '#383e46';
        ctx.font = 'bold 18px Poppins';
        ctx.textAlign = 'center';
        ctx.fillText(this.title, baseWidth / 2, currentY + 20);
        currentY += titleHeight;
        ctx.textAlign = 'left'; // Reset text alignment
      }

      // Draw chart with padding
      const chartWidth = baseWidth - chartPadding * 2;
      const chartHeight = this.$refs.chartRef.$el.height - chartPadding * 2;

      // Enable image smoothing for better quality
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw the chart at its original size
      ctx.drawImage(chartImage, chartPadding, currentY + chartPadding, chartWidth, chartHeight);

      // Draw legend
      let legendY = this.$refs.chartRef.$el.height + currentY + padding;

      // Define column positions for right-aligned values
      const labelColumnWidth = 200;
      const valueColumnX = padding + labelColumnWidth;

      // Draw total value row first
      ctx.fillStyle = '#383e46';
      ctx.font = 'bold 14px Poppins';
      const formattedUnit = this.totalUnit.replace(/CO2|Co2|co2/gi, 'CO₂');
      const totalText = `Total ${this.totalValue} ${formattedUnit}`;
      ctx.fillText('Total', padding, legendY);

      // Right-align total value
      ctx.fillStyle = '#383e46';
      const totalValueText = `${this.totalValue} ${formattedUnit}`;
      const totalValueWidth = ctx.measureText(totalValueText).width;
      ctx.fillText(totalValueText, valueColumnX + 100 - totalValueWidth, legendY);

      // Add separator line
      legendY += 25;
      ctx.beginPath();
      ctx.strokeStyle = '#E5E7EB';
      ctx.moveTo(padding, legendY - 10);
      ctx.lineTo(valueColumnX + 100, legendY - 10); // Extend line beyond the value column
      ctx.stroke();

      // Draw segment rows using utility functions
      this.sortedSegmentsWithCustomColors.forEach((segment, index) => {
        // Draw color box
        ctx.fillStyle = segment.color;
        ctx.fillRect(padding, legendY + index * 25, 15, 15);

        // Use utility function for label truncation
        ctx.fillStyle = '#383e46';
        ctx.font = '14px Poppins';
        const maxLabelWidth = labelColumnWidth - 35; // Account for color box and spacing
        const truncatedLabel = utilTruncateLabel(segment.label, maxLabelWidth);
        ctx.fillText(truncatedLabel, padding + 25, legendY + index * 25 + 12);

        // Right-align value
        ctx.fillStyle = '#838A93';
        const valueText = `${segment.value || 0} ${formattedUnit}`;
        const valueWidth = ctx.measureText(valueText).width;
        ctx.fillText(valueText, valueColumnX + 100 - valueWidth, legendY + index * 25 + 12);
      });

      // Draw logo at the bottom right with text
      const logoY = baseHeight - logoHeight - padding;
      const text = 'Calculated with';
      ctx.font = '10px Poppins';
      ctx.fillStyle = '#838A93';
      const textWidth = ctx.measureText(text).width;
      const totalWidth = textWidth + 10 + logoWidth; // 10px spacing between text and logo
      const logoX = baseWidth - totalWidth - padding;

      // Draw text
      ctx.fillText(text, logoX, logoY + logoHeight / 2 + 5); // Center text vertically with logo

      // Draw logo
      ctx.drawImage(logoImage, logoX + textWidth + 10, logoY, logoWidth, logoHeight);

      // Create download link with high quality
      const link = document.createElement('a');
      link.download = 'spark-carbon-chart.png';
      link.href = compositeCanvas.toDataURL('image/png', 1.0); // Use maximum quality
      link.click();

      // Clean up
      exportChart.destroy();
    },
  },
};
</script>
